import { useState, useEffect, useRef } from "react";

interface NetworkState {
    isOnline: boolean;
    isReconnecting: boolean;
    lastConnectionChange: number;
    shouldSuppressErrors: boolean;
}

interface UseNetworkStateReturn extends NetworkState {
    triggerRetry: () => void;
    isInGracePeriod: () => boolean;
}

const GRACE_PERIOD_MS = 5000; // 5 seconds after network change to suppress errors
const RECONNECT_PERIOD_MS = 10000; // Show reconnecting state for up to 10 seconds

/**
 * Hook to manage network state and provide graceful handling of network transitions
 *
 * This hook helps prevent intrusive error modals when users wake from sleep or
 * experience network changes by providing a grace period during reconnection.
 */
export const useNetworkState = (): UseNetworkStateReturn => {
    const [networkState, setNetworkState] = useState<NetworkState>({
        isOnline: navigator.onLine,
        isReconnecting: false,
        lastConnectionChange: Date.now(),
        shouldSuppressErrors: false,
    });

    const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
    const graceTimeoutRef = useRef<NodeJS.Timeout>();

    // Clear timeouts on unmount
    useEffect(() => {
        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (graceTimeoutRef.current) {
                clearTimeout(graceTimeoutRef.current);
            }
        };
    }, []);

    // Handle network state changes
    useEffect(() => {
        const handleOnline = () => {
            const now = Date.now();

            // Clear any existing timeouts
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (graceTimeoutRef.current) {
                clearTimeout(graceTimeoutRef.current);
            }

            setNetworkState(prev => ({
                ...prev,
                isOnline: true,
                isReconnecting: true,
                lastConnectionChange: now,
                shouldSuppressErrors: true,
            }));

            // End grace period after GRACE_PERIOD_MS
            graceTimeoutRef.current = setTimeout(() => {
                setNetworkState(prev => ({
                    ...prev,
                    shouldSuppressErrors: false,
                }));
            }, GRACE_PERIOD_MS);

            // End reconnecting state after RECONNECT_PERIOD_MS
            reconnectTimeoutRef.current = setTimeout(() => {
                setNetworkState(prev => ({
                    ...prev,
                    isReconnecting: false,
                }));
            }, RECONNECT_PERIOD_MS);
        };

        const handleOffline = () => {
            const now = Date.now();

            // Clear any existing timeouts
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (graceTimeoutRef.current) {
                clearTimeout(graceTimeoutRef.current);
            }

            setNetworkState(prev => ({
                ...prev,
                isOnline: false,
                isReconnecting: false,
                lastConnectionChange: now,
                shouldSuppressErrors: true, // Suppress errors while offline
            }));
        };

        // Listen to network events
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        /*
         * Sleep/wake detection is handled purely by browser's online/offline events
         * When laptop sleeps: 'offline' event fires
         * When laptop wakes: 'online' event fires
         * This is more reliable and doesn't trigger on tab switching
         */

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);

    // Function to manually trigger a retry period
    const triggerRetry = () => {
        const now = Date.now();

        setNetworkState(prev => ({
            ...prev,
            isReconnecting: true,
            lastConnectionChange: now,
            shouldSuppressErrors: true,
        }));

        // Clear existing timeouts
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        if (graceTimeoutRef.current) {
            clearTimeout(graceTimeoutRef.current);
        }

        // End grace period
        graceTimeoutRef.current = setTimeout(() => {
            setNetworkState(prev => ({
                ...prev,
                shouldSuppressErrors: false,
            }));
        }, GRACE_PERIOD_MS);

        // End reconnecting state
        reconnectTimeoutRef.current = setTimeout(() => {
            setNetworkState(prev => ({
                ...prev,
                isReconnecting: false,
            }));
        }, RECONNECT_PERIOD_MS);
    };

    // Function to check if we're currently in grace period
    const isInGracePeriod = () => {
        const timeSinceChange = Date.now() - networkState.lastConnectionChange;
        return networkState.shouldSuppressErrors && timeSinceChange < GRACE_PERIOD_MS;
    };

    return {
        ...networkState,
        triggerRetry,
        isInGracePeriod,
    };
};